import React, { useRef, useEffect } from 'react';
import { Box, Typography, Chip, CircularProgress } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GroupsIcon from '@mui/icons-material/Groups';
import AssistantAvatar from './AssistantAvatar';
import { AT } from './assistantStijl';
import { useAssistant } from '../../contexts/AssistantContext';
import { useLocation } from 'react-router-dom';
import AssistantMessage from './AssistantMessage';
import ConfirmationCard from './ConfirmationCard';

const TOOL_LABELS = {
  find_person: 'Persoon opzoeken...',
  get_my_tasks: 'Taken ophalen...',
  get_week_overview: 'Weekplanning laden...',
  get_person_capacity: 'Capaciteit berekenen...',
  get_person_schedule: 'Planning ophalen...',
  get_unplanned_tasks: 'Ongeplande taken zoeken...',
  get_project_summary: 'Projectinfo ophalen...',
  plan_create_task: 'Taak voorbereiden...',
  plan_update_task: 'Taak bijwerken...',
};

const QUICK_ACTIONS = [
  { label: 'Mijn taken vandaag', icon: <CalendarTodayIcon sx={{ fontSize: 16 }} /> },
  { label: 'Capaciteit deze week', icon: <GroupsIcon sx={{ fontSize: 16 }} /> },
  { label: 'Wat kan je voor me doen?', icon: <AutoFixHighIcon sx={{ fontSize: 16 }} /> },
];

export default function AssistantChatArea() {
  const { messages, pendingAction, toolStatus, sendMessage, isStreaming } = useAssistant();
  const location = useLocation();
  const opProject = /^\/projects\/rec[A-Za-z0-9]{14}/.test(location.pathname);
  const scrollRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, pendingAction, toolStatus]);

  const handleQuickAction = (label) => {
    if (isStreaming) return;
    sendMessage(label);
  };

  // Welcome screen when no messages
  if (messages.length === 0) {
    return (
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          gap: 3,
          overflow: 'auto',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <AssistantAvatar size={44} sx={{ display: 'block', mx: 'auto', mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontFamily: AT.font, fontWeight: 700, mb: 0.5, color: AT.text }}>
            Hoi! Ik ben je Full Frame assistent.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ik kan je helpen met taken, planning en meer.
          </Typography>
          {opProject && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: AT.secondary }}>
              📎 Ik neem de context van dit project mee: offerte, taken, deliverables, draaidagen, notities, meetings en het klantprofiel.
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          {QUICK_ACTIONS.map((action) => (
            <Chip
              key={action.label}
              label={action.label}
              icon={action.icon}
              onClick={() => handleQuickAction(action.label)}
              sx={{
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
                bgcolor: AT.surfaceLow,
                color: AT.secondary,
                border: 0,
                '& .MuiChip-icon': { color: AT.secondary },
                '&:hover': { bgcolor: AT.surfaceHigh, color: AT.text },
              }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      ref={scrollRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {messages.map((msg, idx) => {
        // Show typing indicator for empty streaming assistant messages
        if (msg.role === 'assistant' && !msg.content && msg.isStreaming) {
          return (
            <Box key={msg.id || idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
              <AssistantAvatar />
              <Box sx={{
                display: 'flex', gap: 0.5, alignItems: 'center',
                bgcolor: AT.surfaceLow, px: 2, py: 1.5, borderRadius: '1rem',
              }}>
                {[0, 1, 2].map(i => (
                  <Box key={i} sx={{
                    width: 8, height: 8, borderRadius: '50%', bgcolor: AT.outlineVariant,
                    animation: 'dotPulse 1.4s ease-in-out infinite',
                    animationDelay: `${i * 0.2}s`,
                  }} />
                ))}
              </Box>
            </Box>
          );
        }
        return <AssistantMessage key={msg.id || idx} message={msg} />;
      })}

      {/* Tool status indicator */}
      {toolStatus && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
          <CircularProgress size={14} thickness={5} />
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {TOOL_LABELS[toolStatus.tool] || 'Even denken...'}
          </Typography>
        </Box>
      )}

      {/* Pending action confirmation */}
      {pendingAction && (
        <ConfirmationCard action={pendingAction} />
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes dotPulse {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </Box>
  );
}
